/** Checklists prontos (biblioteca por área): escolher um para usar e a tela de configuração. */
import { useTableSort } from "@/lib/sort";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, Empty, ErrorBox, Field, FormGrid, Loading, Modal, TypeSelect, useAct } from "./kit";
import { Icon } from "./icons";
import { confirmar } from "./feedback";

export interface ChecklistTpl { id: string; name: string; description: string | null; items: string[]; areaIds: string[]; activityTypeId: string | null; matchArea?: boolean; allAreas?: boolean; suggested?: boolean }
export type ChecklistScope = "atividade" | "atendimento";

export function useChecklists(scope: ChecklistScope | null, ctx: { processId?: string | null; caseId?: string | null; activityId?: string | null; areaId?: string | null; typeId?: string | null } = {}) {
  return useQuery({
    queryKey: ["checklists", scope, ctx.processId, ctx.caseId, ctx.activityId, ctx.areaId, ctx.typeId],
    queryFn: () => api.get<ChecklistTpl[]>(`/checklists${qs({ scope: scope ?? undefined, processId: ctx.processId ?? undefined, caseId: ctx.caseId ?? undefined, activityId: ctx.activityId ?? undefined, areaId: ctx.areaId ?? undefined, typeId: ctx.typeId ?? undefined })}`),
    staleTime: 60_000,
  });
}

/** "Usar checklist pronto…": acrescenta os itens do modelo (sem repetir os que já existem). */
export function ChecklistPicker({ scope, ctx, existing = [], onPick, compact }: {
  scope: ChecklistScope | null; ctx?: Parameters<typeof useChecklists>[1]; existing?: string[]; onPick: (items: string[], tpl: ChecklistTpl) => void; compact?: boolean;
}): JSX.Element | null {
  const q = useChecklists(scope, ctx);
  const list = q.data ?? [];
  if (!list.length) return null;
  const sug = list.find((t) => t.suggested);
  return (
    <div className="row nowrap gap-8" style={{ alignItems: "center" }}>
      <select className="input" style={{ maxWidth: compact ? 240 : 360 }} value="" onChange={(e) => {
        const t = list.find((x) => x.id === e.target.value);
        if (!t) return;
        const have = new Set(existing.map((x) => x.trim().toLowerCase()));
        onPick(t.items.filter((x) => !have.has(x.trim().toLowerCase())), t);
      }}>
        <option value="">{sug ? `★ Sugerido: ${sug.name}` : "Usar checklist pronto…"}</option>
        {list.map((t) => <option key={t.id} value={t.id}>{t.suggested ? "★ " : ""}{t.name} ({t.items.length}){t.matchArea ? "" : t.allAreas ? " · todas as áreas" : ""}</option>)}
      </select>
      {sug && !compact && <button type="button" className="btn sm" onClick={() => {
        const have = new Set(existing.map((x) => x.trim().toLowerCase()));
        onPick(sug.items.filter((x) => !have.has(x.trim().toLowerCase())), sug);
      }}>Usar o sugerido</button>}
    </div>
  );
}

/** Checklist com caixas de marcar (atendimento). */
export function ChecklistBoxes({ items, onChange, disabled }: { items: Array<{ t: string; d: boolean; by?: string; at?: string }>; onChange?: (next: Array<{ t: string; d: boolean }>) => void; disabled?: boolean }): JSX.Element | null {
  const { userName } = useLookups();
  if (!items.length) return null;
  const done = items.filter((x) => x.d).length;
  return (
    <div className="stack" style={{ gap: 2 }}>
      <div className="muted small">Checklist {done}/{items.length}</div>
      {items.map((c, i) => (
        <label key={i} className="row nowrap small">
          <input type="checkbox" checked={c.d} disabled={disabled || !onChange} onChange={(e) => onChange?.(items.map((x, j) => ({ t: x.t, d: j === i ? e.target.checked : x.d })))} />
          <span className="grow" style={{ textDecoration: c.d ? "line-through" : undefined }}>{c.t}</span>
          {c.d && c.by && <span className="muted tiny">{userName(c.by).split(" ")[0]}</span>}
        </label>
      ))}
    </div>
  );
}

/** Editor de lista (itens a marcar) usado nos formulários: modelo pronto + itens avulsos. */
export function ChecklistEditor({ scope, ctx, value, onChange }: { scope: ChecklistScope; ctx?: Parameters<typeof useChecklists>[1]; value: Array<{ t: string; d: boolean }>; onChange: (v: Array<{ t: string; d: boolean }>) => void }): JSX.Element {
  const [item, setItem] = useState("");
  return (
    <div className="stack" style={{ gap: 4 }}>
      <ChecklistPicker scope={scope} ctx={ctx} existing={value.map((x) => x.t)} onPick={(items) => onChange([...value, ...items.map((t) => ({ t, d: false }))])} />
      {value.map((c, i) => (
        <div key={i} className="row nowrap small">
          <input type="checkbox" checked={c.d} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, d: e.target.checked } : x)))} />
          <span className="grow">{c.t}</span>
          <button type="button" className="link-btn small" onClick={() => onChange(value.filter((_, j) => j !== i))}>tirar</button>
        </div>
      ))}
      <input className="input" placeholder="+ item (Enter)" value={item} onChange={(e) => setItem(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && item.trim()) { e.preventDefault(); onChange([...value, { t: item.trim(), d: false }]); setItem(""); } }} />
    </div>
  );
}

// ------------------------------------------------------------------ configuração

interface TplRow extends ChecklistTpl { scopes: string[]; active: boolean }

export function ChecklistSettings(): JSX.Element {
  const { areaName, typeOf } = useLookups();
  const q = useQuery({ queryKey: ["checklists-config"], queryFn: () => api.get<TplRow[]>("/config/checklists") });
  const [edit, setEdit] = useState<TplRow | "new" | null>(null);
  const [area, setArea] = useState("");
  const { catalog } = useLookups();
  const ts = useTableSort("checklists-config", (q.data ?? []).filter((t) => !area || !t.areaIds.length || t.areaIds.includes(area)), {
    nome: (t) => t.name, areas: (t) => (t.areaIds.length ? t.areaIds.map((a) => areaName(a)).join(", ") : "Todas"), itens: (t) => t.items.length, situacao: (t) => (t.active ? "ativo" : "inativo"),
  });
  const list = ts.rows;
  return (
    <Card title="Checklists prontos" actions={<button className="btn primary" onClick={() => setEdit("new")}><Icon name="plus" size="sm" /> Novo checklist</button>}>
      <div className="muted small" style={{ marginBottom: 10 }}>
        Listas de verificação por área de atuação (ex.: “Documentos para aposentadoria”, “Conferência antes do protocolo”). Usadas ao criar atividades,
        nas tarefas do fluxograma e nos atendimentos. Usar um checklist copia os itens — mudar o modelo depois não altera o que já foi usado.
        Com “tipo de atividade”, ele aparece como sugestão (★) ao criar uma atividade desse tipo na área.
      </div>
      <div className="row gap-8" style={{ marginBottom: 10 }}>
        <select className="input" style={{ maxWidth: 260 }} value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="">Todas as áreas</option>{(catalog?.areas ?? []).filter((a) => a.active).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <ErrorBox error={q.error} />
      {!q.data ? <Loading /> : !list.length ? <Empty icon="tick">Nenhum checklist ainda.</Empty> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>{ts.th("Checklist", "nome")}{ts.th("Áreas", "areas")}<th>Usado em</th>{ts.th("Itens", "itens")}{ts.th("Situação", "situacao")}</tr></thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => setEdit(t)}>
                  <td><div className="strong">{t.name}</div>{t.activityTypeId && <div className="muted small">sugerido para: {typeOf(t.activityTypeId)?.name ?? "—"}</div>}</td>
                  <td className="small">{t.areaIds.length ? t.areaIds.map((a) => areaName(a)).join(", ") : "Todas"}</td>
                  <td className="small">{t.scopes.map((s) => (s === "atividade" ? "Atividades" : "Atendimentos")).join(", ")}</td>
                  <td>{t.items.length}</td>
                  <td>{t.active ? <Badge tone="ok">ativo</Badge> : <Badge tone="muted">inativo</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <ChecklistModal tpl={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function ChecklistModal({ tpl, onClose }: { tpl: TplRow | null; onClose: () => void }): JSX.Element {
  const { catalog } = useLookups();
  const [name, setName] = useState(tpl?.name ?? "");
  const [description, setDescription] = useState(tpl?.description ?? "");
  const [areaIds, setAreaIds] = useState<string[]>(tpl?.areaIds ?? []);
  const [scopes, setScopes] = useState<string[]>(tpl?.scopes ?? ["atividade", "atendimento"]);
  const [typeId, setTypeId] = useState<string | null>(tpl?.activityTypeId ?? null);
  const [text, setText] = useState((tpl?.items ?? []).join("\n"));
  const [active, setActive] = useState(tpl?.active ?? true);
  const items = text.split("\n").map((x) => x.trim()).filter(Boolean);
  const body = () => ({ name, description: description || null, areaIds, scopes, activityTypeId: typeId, items, active });
  const inv = [["checklists-config"], ["checklists"]];
  const save = useAct(() => (tpl ? api.patch(`/config/checklists/${tpl.id}`, body()) : api.post("/config/checklists", body())), inv, onClose, "Checklist salvo");
  const del = useAct(() => api.del(`/config/checklists/${tpl!.id}`), inv, onClose, "Checklist excluído");
  return (
    <Modal title={tpl ? "Checklist" : "Novo checklist"} onClose={onClose} wide footer={<>
      {tpl && <button className="btn danger" style={{ marginRight: "auto" }} onClick={() => void (async () => { if (await confirmar(`Excluir “${tpl.name}”? O que já foi usado não muda.`, { danger: true, confirm: "Excluir" })) del.mutate(); })()}>Excluir</button>}
      <button className="btn" onClick={onClose}>Cancelar</button>
      <button className="btn primary" disabled={save.isPending || name.trim().length < 2 || !items.length || !scopes.length} onClick={() => save.mutate()}>Salvar</button>
    </>}>
      <ErrorBox error={save.error ?? del.error} />
      <FormGrid cols={2}>
        <Field label="Nome" required span={2}><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Ex.: Documentos — aposentadoria por idade" /></Field>
        <Field label="Áreas de atuação" span={2} hint="Nenhuma marcada = vale para todas.">
          <div className="row wrap gap-8">
            {(catalog?.areas ?? []).filter((a) => a.active).map((a) => (
              <label key={a.id} className="row gap-4 small"><input type="checkbox" checked={areaIds.includes(a.id)} onChange={(e) => setAreaIds(e.target.checked ? [...areaIds, a.id] : areaIds.filter((x) => x !== a.id))} /> {a.name}</label>
            ))}
          </div>
        </Field>
        <Field label="Usar em">
          <div className="row gap-8">
            {[["atividade", "Atividades e tarefas de fluxo"], ["atendimento", "Atendimentos"]].map(([k, l]) => (
              <label key={k} className="row gap-4 small"><input type="checkbox" checked={scopes.includes(k!)} onChange={(e) => setScopes(e.target.checked ? [...scopes, k!] : scopes.filter((x) => x !== k))} /> {l}</label>
            ))}
          </div>
        </Field>
        <Field label="Sugerir para o tipo de atividade" hint="Opcional: aparece com ★ ao criar uma atividade desse tipo."><TypeSelect value={typeId} onChange={setTypeId} /></Field>
        <Field label="Itens (um por linha)" required span={2}><textarea className="input" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={"RG e CPF\nComprovante de residência\nCTPS (todas)\nCNIS"} /></Field>
        <Field label="Descrição" span={2}><input className="input" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <label className="row gap-4 small"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Ativo</label>
        <div className="muted small">{items.length} item(ns)</div>
      </FormGrid>
    </Modal>
  );
}
